import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DataService } from '../../../../../../services/commande-client/data.service';
import { AchatService } from '../../../../../../services/achat/achat.service';
import { DocumentService } from '../../../../../../services/document/document.service';
import { FournisseurService } from '../../../../../../services/Fournisseur/fournisseur.service';
import { PaiementService } from '../../../../../../services/paiement/paiement.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import flatpickr from 'flatpickr';
import { French } from 'flatpickr/dist/l10n/fr';
import { forkJoin } from 'rxjs';

interface Item {
    reference: string;
    date: string;
    matricule: string;
    client?: string;
    revenue: string;
    status: string;
}

/** Ligne document pour le tableau paiements non affectés (documents par fournisseur + statut) */
export interface DocumentRow {
    id: number;
    reference: string;
    date: string;
    methodPaiement: string;
    documentType: string;
    montantTotal: string;
    montantRestant: string;
    raw?: any;
}

interface Filters {
    type?: string;
    raison?: string;
    matricule?: string;
    chiffre_aff?: string;
    operator?: string;
    indebted?: string;
    fournisseur?: string;
    projet?: string;
    montant?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
}
@Component({
  selector: 'app-fr-detail-contenu-paiement-non-aff',
  templateUrl: './fr-detail-contenu-paiement-non-aff.component.html',
  styleUrls: ['./fr-detail-contenu-paiement-non-aff.component.scss']
})
export class FrDetailContenuPaiementNonAffComponent implements OnInit {
    viewMode: 'jours' | 'mois' | 'annees' = 'jours';
    weekDays: { label: string; dateString: string; isToday: boolean }[] = [];
    months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    years: number[] = [];
    currentMonthIndex = new Date().getMonth();
    currentYear = new Date().getFullYear();
    selectedMonthIndex: number | null = null;
    selectedDay: string = '';
    selectedYear: number | null = null;
    selectedDate: Date | null = null;
    selectedPeriod: string = '';
    searchTerm: string = '';
    showFilter: boolean = false;
    filters: Filters = { operator: '=', type: '', raison: '', matricule: '', chiffre_aff: '', indebted: '', fournisseur: '', projet: '', montant: '', status: '', dateFrom: '', dateTo: '' };
    paginatedData: Item[] = [];
    statuses: string[] = ['Tous', 'Brouillon', 'Créé', 'Annulé', 'Négociation', 'Accepté', 'Valide', 'Livré', 'Non Payé', 'Payé', 'Fermé', 'Reçu'];
    isExportingPDF: boolean = false;
    baseRoute: string = '/achat';
    type: string = '';

    /** Documents par fournisseur + statut (statut_id 12 et 15) */
    readonly STATUT_IDS_PAIEMENT_NON_AFFECTE: number[] = [12, 15];
    fournisseurId: number | null = null;
    documents: DocumentRow[] = [];
    /** Liste paginée affichée dans le tableau (même design que shared) */
    paginatedDocuments: DocumentRow[] = [];
    isLoadingDocuments: boolean = false;

    /** Pagination (même design que shared) */
    basicSelectedOption = 10;
    currentPage = 1;
    totalPages = 1;
    pages: number[] = [];

    @ViewChild('datePicker') datePicker!: ElementRef;
    @ViewChild('dateFromPicker') dateFromPicker!: ElementRef;
    @ViewChild('dateToPicker') dateToPicker!: ElementRef;

    constructor(
        private toastr: ToastrService,
        private dataService: DataService,
        private achatService: AchatService,
        private documentService: DocumentService,
        private fournisseurService: FournisseurService,
        private paiementService: PaiementService,
        private cdr: ChangeDetectorRef,
        private route: ActivatedRoute,
        private router: Router,
        private modalService: NgbModal
    ) {}

    ngOnInit(): void {
        const today = new Date();
        this.selectedDate = today;
        this.selectedDay = today.toISOString().split('T')[0];
        this.selectedMonthIndex = today.getMonth();
        this.selectedYear = today.getFullYear();
        this.updateSelectedPeriod();
        this.generateWeekDays();
        this.years = Array.from({ length: 10 }, (_, i) => this.currentYear - i).reverse();
        this.initializeFlatpickr();
        const reference = this.route.parent?.snapshot.paramMap.get('reference');
        if (reference && reference !== 'add') {
            this.resolveFournisseurIdAndLoadDocuments(reference);
        }
        this.route.params.subscribe(params => {
            this.type = params['type'] || 'facture';
            this.baseRoute = '/achat';
            this.loadData();
        });
    }

    /** Résout fournisseur_id à partir de reference (id ou identifiant_unique) puis charge les documents. */
    private resolveFournisseurIdAndLoadDocuments(reference: string): void {
        const numId = parseInt(reference, 10);
        if (!isNaN(numId) && String(numId) === reference) {
            this.fournisseurId = numId;
            this.loadDocuments();
            return;
        }
        this.fournisseurService.getFournisseurById(reference).subscribe({
            next: (fournisseur) => {
                this.fournisseurId = fournisseur.id ?? null;
                if (this.fournisseurId != null) this.loadDocuments();
            },
            error: () => {
                this.toastr.error('Fournisseur introuvable', 'Erreur');
            }
        });
    }

    /** Charge tous les documents du fournisseur avec statut_id 12 et 15 (toutes les pages). */
    loadDocuments(): void {
        if (this.fournisseurId == null) return;
        this.isLoadingDocuments = true;
        this.documents = [];
        const requests = this.STATUT_IDS_PAIEMENT_NON_AFFECTE.map(statutId =>
            this.documentService.getDocumentsByFournisseurStatutAll(this.fournisseurId!, statutId)
        );
        forkJoin(requests).subscribe({
            next: (responses) => {
                const allDocs: any[] = (responses as any[][]).reduce((acc, arr) => acc.concat(arr), []);
                if (allDocs.length === 0) {
                    this.documents = [];
                    this.isLoadingDocuments = false;
                    this.updatePagination();
                    this.cdr.detectChanges();
                    return;
                }
                const paiementRequests = allDocs.map((doc: any) => this.paiementService.getPaiementsByDocumentId(doc.id));
                forkJoin(paiementRequests).subscribe({
                    next: (paiementResponses) => {
                        this.documents = allDocs.map((doc: any, index: number) => {
                            const totalPaye = this.computeTotalPayeFromResponse(paiementResponses[index]);
                            const netPayer = parseFloat(doc.net_payer || doc.total_ttc || '0') || 0;
                            const resteAPayer = Math.max(0, netPayer - totalPaye);
                            return {
                                id: doc.id,
                                reference: doc.ref_externe ?? doc.reference ?? '—',
                                date: this.formatDocumentDate(doc.date),
                                methodPaiement: (doc.methode_paiement ?? doc.methodePaiement ?? this.getMethodePaiementFromResponse(paiementResponses[index])) || '—',
                                documentType: doc.typeDocument?.titre ?? doc.type_document_id ?? '—',
                                montantTotal: this.formatMontantDoc(doc.net_payer ?? doc.total_ttc),
                                montantRestant: this.formatMontantDoc(resteAPayer),
                                raw: doc
                            };
                        });
                        this.isLoadingDocuments = false;
                        this.updatePagination();
                        this.cdr.detectChanges();
                    },
                    error: () => {
                        this.documents = allDocs.map((doc: any) => ({
                            id: doc.id,
                            reference: doc.ref_externe ?? doc.reference ?? '—',
                            date: this.formatDocumentDate(doc.date),
                            methodPaiement: doc.methode_paiement ?? doc.methodePaiement ?? '—',
                            documentType: doc.typeDocument?.titre ?? doc.type_document_id ?? '—',
                            montantTotal: this.formatMontantDoc(doc.net_payer ?? doc.total_ttc),
                            montantRestant: this.formatMontantDoc(doc.net_payer ?? '—'),
                            raw: doc
                        }));
                        this.isLoadingDocuments = false;
                        this.updatePagination();
                        this.cdr.detectChanges();
                    }
                });
            },
            error: () => {
                this.isLoadingDocuments = false;
                this.toastr.error('Erreur lors du chargement des documents', 'Erreur');
                this.cdr.detectChanges();
            }
        });
    }

    /** Met à jour la liste paginée (même logique que shared). */
    updatePagination(): void {
        if (this.isLoadingDocuments) return;
        this.totalPages = Math.ceil(this.documents.length / this.basicSelectedOption) || 1;
        this.currentPage = Math.min(this.currentPage, this.totalPages);
        this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        const start = (this.currentPage - 1) * this.basicSelectedOption;
        this.paginatedDocuments = this.documents.slice(start, start + this.basicSelectedOption);
        this.cdr.detectChanges();
    }

    changePage(page: number): void {
        this.currentPage = page;
        this.updatePagination();
        this.cdr.detectChanges();
    }

    private computeTotalPayeFromResponse(response: any): number {
        let paiements: any[] = [];
        if (Array.isArray(response)) {
            paiements = response;
        } else if (response?.data) {
            if (Array.isArray(response.data)) paiements = response.data;
            else if (Array.isArray(response.data.paiements)) paiements = response.data.paiements;
            else if (response.data.paiement && typeof response.data.paiement === 'object') paiements = [response.data.paiement];
        } else if (response?.id) {
            paiements = [response];
        }
        let totalPaye = 0;
        for (const paiement of paiements) {
            for (const ligne of paiement.lignes_paiement || []) {
                totalPaye += parseFloat(ligne.montant || '0') || 0;
            }
        }
        return totalPaye;
    }

    /** Extrait la méthode de paiement depuis la réponse getPaiementsByDocumentId (document ou lignes de paiement). */
    private getMethodePaiementFromResponse(response: any): string {
        let paiements: any[] = [];
        if (Array.isArray(response)) {
            paiements = response;
        } else if (response?.data) {
            if (Array.isArray(response.data)) paiements = response.data;
            else if (Array.isArray(response.data.paiements)) paiements = response.data.paiements;
            else if (response.data.paiement && typeof response.data.paiement === 'object') paiements = [response.data.paiement];
        } else if (response?.id) {
            paiements = [response];
        }
        const methodes: string[] = [];
        for (const paiement of paiements) {
            const mp = paiement.methode_paiement ?? paiement.methodePaiement ?? paiement.type_paiement;
            if (mp && !methodes.includes(mp)) methodes.push(String(mp));
            for (const ligne of paiement.lignes_paiement || []) {
                const lp = ligne.type_paiement ?? ligne.methode_paiement;
                if (lp && !methodes.includes(lp)) methodes.push(String(lp));
            }
        }
        if (methodes.length === 0) return '';
        return methodes.map(m => this.formatMethodePaiementLabel(m)).join(', ');
    }

    private formatMethodePaiementLabel(code: string): string {
        const labels: { [key: string]: string } = {
            espece: 'Espèce',
            cheque: 'Chèque',
            virement: 'Virement',
            traite: 'Traite',
            retenue: 'Retenue'
        };
        return labels[code?.toLowerCase()] ?? (code && code.charAt(0).toUpperCase() + code.slice(1).toLowerCase()) ?? code;
    }

    private formatDocumentDate(dateStr: string | undefined): string {
        if (!dateStr) return '—';
        try {
            const d = new Date(dateStr);
            return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch {
            return dateStr;
        }
    }

    private formatMontantDoc(value: string | number | undefined): string {
        if (value == null || value === '') return '—';
        const n = typeof value === 'string' ? parseFloat(value) : value;
        return isNaN(n) ? '—' : n.toFixed(3).replace('.', ',') + ' TND';
    }

    generateWeekDays(): void {
        const today = new Date();
        const startOfWeek = new Date(this.selectedDate || today);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);
        this.weekDays = Array.from({ length: 7 }, (_, i) => {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            return {
                label: d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' }),
                dateString: d.toISOString().split('T')[0],
                isToday: d.toDateString() === today.toDateString(),
            };
        });
    }

    setViewMode(mode: 'jours' | 'mois' | 'annees'): void {
        this.viewMode = mode;
        this.selectedPeriod = '';
        if (mode === 'jours') {
            this.selectedDay = this.selectedDate?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0];
            this.updateSelectedPeriod();
            this.loadData(this.selectedDay, this.selectedDay);
        } else if (mode === 'mois') {
            this.selectedMonthIndex = this.selectedDate?.getMonth() ?? this.currentMonthIndex;
            this.selectMonth(this.selectedMonthIndex);
        } else {
            this.selectedYear = this.selectedDate?.getFullYear() ?? this.currentYear;
            this.selectYear(this.selectedYear);
        }
        this.cdr.detectChanges();
    }

    selectDay(day: { dateString: string }): void {
        this.selectedDay = day.dateString;
        this.selectedDate = new Date(day.dateString);
        this.selectedMonthIndex = this.selectedDate.getMonth();
        this.selectedYear = this.selectedDate.getFullYear();
        this.updateSelectedPeriod();
        this.loadData(this.selectedDay, this.selectedDay);
    }

    selectMonth(index: number): void {
        this.selectedMonthIndex = index;
        const year = this.selectedYear ?? this.currentYear;
        const monthStart = new Date(year, index, 1).toISOString().split('T')[0];
        const monthEnd = new Date(year, index + 1, 0).toISOString().split('T')[0];
        this.selectedPeriod = `${this.months[index]} ${year}`;
        this.selectedDate = new Date(year, index, 1);
        this.loadData(monthStart, monthEnd);
    }

    selectYear(year: number): void {
        this.selectedYear = year;
        const yearStart = `${year}-01-01`;
        const yearEnd = `${year}-12-31`;
        this.selectedPeriod = `Année: ${year}`;
        this.selectedDate = new Date(year, this.selectedMonthIndex ?? 0, 1);
        this.loadData(yearStart, yearEnd);
    }

    incrementDate(): void {
        if (!this.selectedDate) return;
        if (this.viewMode === 'mois') {
            this.selectedDate.setMonth(this.selectedDate.getMonth() + 1);
            this.selectedMonthIndex = this.selectedDate.getMonth();
        } else if (this.viewMode === 'annees') {
            this.selectedDate.setFullYear(this.selectedDate.getFullYear() + 1);
            this.selectedYear = this.selectedDate.getFullYear();
        } else {
            this.selectedDate.setDate(this.selectedDate.getDate() + 1);
            this.selectedDay = this.selectedDate.toISOString().split('T')[0];
        }
        this.updateSelectedPeriod();
        this.generateWeekDays();
        this.loadData(this.selectedDay, this.selectedDay);
    }

    decrementDate(): void {
        if (!this.selectedDate) return;
        if (this.viewMode === 'mois') {
            this.selectedDate.setMonth(this.selectedDate.getMonth() - 1);
            this.selectedMonthIndex = this.selectedDate.getMonth();
        } else if (this.viewMode === 'annees') {
            this.selectedDate.setFullYear(this.selectedDate.getFullYear() - 1);
            this.selectedYear = this.selectedDate.getFullYear();
        } else {
            this.selectedDate.setDate(this.selectedDate.getDate() - 1);
            this.selectedDay = this.selectedDate.toISOString().split('T')[0];
        }
        this.updateSelectedPeriod();
        this.generateWeekDays();
        this.loadData(this.selectedDay, this.selectedDay);
    }

    initializeFlatpickr(): void {
        if (this.datePicker?.nativeElement) {
            flatpickr(this.datePicker.nativeElement, {
                locale: French,
                mode: 'range',
                dateFormat: 'Y-m-d',
                defaultDate: [new Date(), new Date()],
                onChange: (selectedDates: Date[]) => {
                    if (selectedDates.length === 2) {
                        const startDate = selectedDates[0].toISOString().split('T')[0];
                        const endDate = selectedDates[1].toISOString().split('T')[0];
                        this.selectedPeriod = `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
                        this.selectedMonthIndex = null;
                        this.selectedDay = '';
                        this.selectedYear = null;
                        this.selectedDate = null;
                        this.loadData(startDate, endDate);
                    }
                },
            });
        }
    }

    initializeFlatpickrDate(field: 'dateFrom' | 'dateTo'): void {
        const picker = field === 'dateFrom' ? this.dateFromPicker : this.dateToPicker;
        if (picker?.nativeElement) {
            flatpickr(picker.nativeElement, {
                locale: French,
                dateFormat: 'Y-m-d',
                onChange: (selectedDates: Date[]) => {
                    if (selectedDates[0]) {
                        this.filters[field] = selectedDates[0].toISOString().split('T')[0];
                        this.applyFilters();
                    }
                },
            });
        }
    }

    openFlatpickr(): void {
        if (this.datePicker?.nativeElement?._flatpickr) {
            this.datePicker.nativeElement._flatpickr.open();
        }
    }

    formatDate(dateString: string): string {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    }

    updateSelectedPeriod(): void {
        if (!this.selectedDate) {
            this.selectedPeriod = 'Aujourd’hui';
            return;
        }
        const month = this.months[this.selectedDate.getMonth()];
        const day = this.selectedDate.getDate();
        const year = this.selectedDate.getFullYear();
        this.selectedPeriod = this.viewMode === 'annees' ? `Année: ${year}` : `${day} ${month} ${year}`;
    }

    toggleFilter(): void {
        this.showFilter = !this.showFilter;
    }

    onSearch(): void {
        this.loadData();
    }

    applyFilters(): void {
        this.loadData();
        this.toggleFilter();
    }

    resetFilters(): void {
        this.filters = { operator: '=', type: '', raison: '', matricule: '', chiffre_aff: '', indebted: '', fournisseur: '', projet: '', montant: '', status: '', dateFrom: '', dateTo: '' };
        const today = new Date();
        this.selectedDate = today;
        this.selectedDay = today.toISOString().split('T')[0];
        this.selectedMonthIndex = today.getMonth();
        this.selectedYear = today.getFullYear();
        this.updateSelectedPeriod();
        this.loadData(this.selectedDay, this.selectedDay);
        if (this.datePicker?.nativeElement?._flatpickr) {
            this.datePicker.nativeElement._flatpickr.setDate([today, today], true);
        }
        if (this.dateFromPicker?.nativeElement?._flatpickr) {
            this.dateFromPicker.nativeElement._flatpickr.clear();
        }
        if (this.dateToPicker?.nativeElement?._flatpickr) {
            this.dateToPicker.nativeElement._flatpickr.clear();
        }
    }

    getBaseRoute(): string {
        return this.baseRoute;
    }

    getStatusClass(status: string): string {
        const statusMap: { [key: string]: string } = {
            Tous: 'badge-tous',
            Brouillon: 'badge-brouillon',
            Créé: 'badge-cree',
            Annulé: 'badge-annule',
            Négociation: 'badge-negociation',
            Accepté: 'badge-accepte',
            Valide: 'badge-valide',
            Livré: 'badge-livre',
            'Non Payé': 'badge-non-paye',
            Payé: 'badge-paye',
            Fermé: 'badge-ferme',
            Reçu: 'badge-recu',
        };
        return statusMap[status] || 'badge-default';
    }

    loadData(startDate: string = this.selectedDay, endDate: string = this.selectedDay): void {
        const service = this.type && ['commande-fournisseur', 'bon-reception', 'facture-fournisseur'].includes(this.type) ? this.achatService : this.dataService;
        const filters = { ...this.filters, dateFrom: startDate, dateTo: endDate, search: this.searchTerm };

    }

    openApercueModal(item: Item): void {
        // Implement modal opening logic
        this.toastr.info(`Ouverture de l'aperçu pour ${item.reference}`, 'Info');
    }

    openDetailModal(item: Item): void {
        // Implement modal opening logic
        this.toastr.info(`Ouverture des détails pour ${item.reference}`, 'Info');
    }

    openPaiementModal(item: Item): void {
        this.toastr.info(`Ouverture du modal de paiement pour ${item.reference}`, 'Info');
    }

    /** Ouvre la page paiement facture fournisseur pour le document. */
    openPaiementModalForDocument(doc: DocumentRow): void {
        if (doc.raw?.id) {
            this.router.navigate(['/achat/facture-fournisseur'], { queryParams: { documentId: doc.raw.id } });
        } else {
            this.toastr.info(`Paiement pour ${doc.reference}`, 'Info');
        }
    }

    openConvertModal(item: Item): void {
        // Implement modal opening logic
        this.toastr.info(`Ouverture du modal de conversion pour ${item.reference}`, 'Info');
    }

    openPhoneModal(item: Item): void {
        // Implement modal opening logic
        this.toastr.info(`Ouverture du modal téléphone pour ${item.reference}`, 'Info');
    }

    openSmsModal(item: Item): void {
        // Implement modal opening logic
        this.toastr.info(`Ouverture du modal SMS pour ${item.reference}`, 'Info');
    }

    openEmailModal(item?: Item): void {
        // Implement modal opening logic
        this.toastr.info(`Ouverture du modal email${item ? ` pour ${item.reference}` : ''}`, 'Info');
    }

    openImportModal(item: Item): void {
        // Implement modal opening logic
        this.toastr.info(`Ouverture du modal d'importation pour ${item.reference}`, 'Info');
    }

    downloadApercuPDF(item: Item): void {
        this.isExportingPDF = true;
        // Implement PDF download logic
        setTimeout(() => {
            this.isExportingPDF = false;
            this.toastr.success(`PDF aperçu téléchargé pour ${item.reference}`, 'Succès');
            this.cdr.detectChanges();
        }, 1000);
    }

    downloadPDF(): void {
        this.isExportingPDF = true;
        // Implement PDF table download logic
        setTimeout(() => {
            this.isExportingPDF = false;
            this.toastr.success('PDF tableau téléchargé', 'Succès');
            this.cdr.detectChanges();
        }, 1000);
    }

    duplicateItem(item: Item): void {
        // Implement duplication logic
        this.toastr.success(`Item ${item.reference} dupliqué`, 'Succès');
    }
}
